import React, { JSX } from "react";
import classNames from "classnames";
import { ShowWhen } from "../show-when/show-when";
import { EditIcon, Info2Icon } from "@/components/assets/icons";
import { useTranslations } from "next-intl";

type ToggleProps = {
  id?: string;
  icon?: JSX.Element | string;
  title?: string;
  subTitle?: number | string;
  isOn: boolean;
  handleToggle: () => void;
  disabled?: boolean;
  noBorder?: boolean;
  containerClassName?: string;
  titleClassName?: string;
  reverse?: boolean;
  handleEdit?: () => void;
  startSymbol?: string;
  endSymbol?: string;
  serviceId?: number;
};

const Toggle: React.FC<ToggleProps> = ({
  id,
  icon,
  title,
  subTitle,
  serviceId,
  isOn,
  handleToggle,
  disabled,
  noBorder = false,
  containerClassName,
  titleClassName,
  reverse,
  handleEdit,
  startSymbol,
  endSymbol,
}) => {
  const t = useTranslations();

  return (
    <div
      className={classNames(
        "flex cursor-pointer items-center justify-between pb-[10px]",
        containerClassName,
        {
          "border-b-[1px] border-[#efefef]": !noBorder,
          "flex-row-reverse": reverse,
        },
      )}
    >
      <label
        className={classNames(
          "flex items-center justify-start text-base font-normal text-black",
        )}
        htmlFor={id}
      >
        <ShowWhen condition={!!icon}>
          <div
            className={classNames(
              "mr-[10px] flex h-[38px] min-h-[38px] w-[38px] min-w-[38px] items-center justify-center rounded-[8px]",
              "primary-light-bg primary-color",
            )}
          >
            {icon}
          </div>
        </ShowWhen>
        <div>
          <ShowWhen condition={!!title}>
            <div className="flex flex-row items-center gap-2">
              <p
                className={classNames(
                  "block text-xs font-medium text-black",
                  titleClassName,
                )}
              >
                {title}
              </p>
              {serviceId === 211 && (
                <a
                  href="https://lex.uz/ru/docs/7357270#"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Info2Icon />
                </a>
              )}
            </div>
          </ShowWhen>
          <ShowWhen
            condition={!!subTitle}
            elseRender={
              <span className="text-sm font-normal text-black opacity-60">
                {subTitle === 0 && t("free")}
              </span>
            }
          >
            <span className="text-sm font-normal text-black opacity-60">
              {startSymbol}
              {subTitle}
              {endSymbol}
            </span>
          </ShowWhen>
        </div>
      </label>
      {handleEdit && (
        <div
          className="ml-auto mr-[24px] flex h-[24px] w-[48px] cursor-pointer items-center justify-center rounded-full bg-[#eef3f4] hover:bg-[#d9d9d9]"
          onClick={!disabled ? handleEdit : undefined}
        >
          <EditIcon className="primary-color rounded-full" />
        </div>
      )}

      <div
        className={classNames(
          "relative inline-flex h-[24px] w-[48px] cursor-pointer items-center rounded-full p-[2px] transition-colors duration-300 ease-in-out",
          isOn ? "bg-[#eef3f4]" : "bg-[#d9d9d9]",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        )}
        onClick={!disabled ? handleToggle : undefined}
      >
        <span
          role="button"
          id={id}
          className={classNames(
            "inline-block h-[20px] w-[20px] transform rounded-full shadow-lg transition-transform duration-300 ease-in-out",
            isOn ? "primary-bg translate-x-[24px]" : "translate-x-0 bg-white",
          )}
        />
      </div>
    </div>
  );
};

export { Toggle };
